import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Heart, ChevronLeft, Check, ArrowRight } from "lucide-react";

export default function CreateEvent() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ partner_one: "", partner_two: "", date: "", venue_name: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/templates").then(({ data }) => setTemplates(data)); }, []);

  const groups = templates.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {});

  const create = async () => {
    if (!selected || !form.partner_one) { toast.error("Please enter a name"); return; }
    setSaving(true);
    try {
      const payload = { type: selected.key, partner_one: form.partner_one,
        partner_two: selected.dual ? form.partner_two : "", date: form.date || null, venue_name: form.venue_name || null };
      const { data } = await api.post("/events", payload);
      toast.success("Event created! Unlock features to start planning.");
      navigate(`/e/${data.id}/pricing`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F5F4F0" }} data-testid="create-event-page">
      <header className="border-b border-[#E8E6E1] bg-white/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-3">
          <Link to="/app" data-testid="back-to-events" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800"><ChevronLeft size={16} /> My Events</Link>
          <div className="flex items-center gap-2 ml-auto"><Heart size={16} className="text-[#8C9A86]" fill="#8C9A86" /><span className="font-serif text-xl">Evermore</span></div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <p className="overline">New event</p>
        <h1 className="font-serif text-4xl tracking-tight">What are we celebrating?</h1>
        <p className="text-stone-500 mt-3">Choose a starter template and we'll tailor the schedule and checklist. Creating an event is free.</p>

        {Object.entries(groups).map(([cat, list]) => (
          <div key={cat} className="mt-8">
            <p className="overline mb-3">{cat}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((t) => (
                <button key={t.key} onClick={() => setSelected(t)} data-testid={`template-${t.key}`}
                  className={`text-left rounded-xl border p-4 transition-all ${selected?.key === t.key ? "border-[#8C9A86] ring-2 ring-[#8C9A86]/30 bg-[#8C9A86]/5" : "border-[#E8E6E1] bg-white hover:border-[#8C9A86]/50"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-xl">{t.name}</span>
                    {selected?.key === t.key && <Check size={16} className="text-[#8C9A86]" />}
                  </div>
                  <p className="text-xs text-stone-500 mt-1">{t.blurb}</p>
                </button>
              ))}
            </div>
          </div>
        ))}

        {selected && (
          <div className="mt-10 bg-white border border-[#E8E6E1] rounded-xl p-6 fade-up" data-testid="create-form">
            <p className="overline mb-4">A few details for your {selected.name}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={selected.primary_label} value={form.partner_one} onChange={(v) => setForm({ ...form, partner_one: v })} testid="primary-input" />
              {selected.dual && <Field label={selected.secondary_label} value={form.partner_two} onChange={(v) => setForm({ ...form, partner_two: v })} testid="secondary-input" />}
              <Field label="Date (optional)" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="date-input" />
              <Field label="Venue (optional)" value={form.venue_name} onChange={(v) => setForm({ ...form, venue_name: v })} testid="venue-input" />
            </div>
            <button onClick={create} disabled={saving} data-testid="create-submit-btn"
              className="mt-6 flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-6 py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50">
              {saving ? "Creating…" : "Create event"} <ArrowRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
